'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Bell,
  Shield,
  Lock,
  Smartphone,
  Monitor,
  Save,
  Edit3,
  X,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Eye,
  EyeOff,
  Key,
  LogOut,
  Clock,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import DirectoryTopbar from '@/app/loan-aggregator-directory/components/DirectoryTopbar';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContactForm {
  fullName: string;
  phone: string;
  city: string;
  state: string;
}

interface NotificationPrefs {
  emailUpdates: boolean;
  smsAlerts: boolean;
  loanOffers: boolean;
  payoutAlerts: boolean;
  referralActivity: boolean;
  weeklyDigest: boolean;
}

// ─── Section: Profile Header ──────────────────────────────────────────────────

function ProfileHeader({ user }: { user: any }) {
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <div className="bg-card rounded-2xl border border-border p-6 flex items-center gap-5">
      {user?.user_metadata?.avatar_url ? (
        <img
          src={user.user_metadata.avatar_url}
          alt={displayName}
          className="w-20 h-20 rounded-2xl object-cover ring-4 ring-primary/10 flex-shrink-0"
        />
      ) : (
        <div className="w-20 h-20 rounded-2xl bg-primary flex items-center justify-center text-white text-2xl font-bold ring-4 ring-primary/10 flex-shrink-0">
          {initials}
        </div>
      )}
      <div className="min-w-0">
        <h2 className="text-xl font-bold text-foreground truncate">{displayName}</h2>
        <p className="text-sm text-muted-foreground mt-0.5 truncate">{user?.email}</p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
            <CheckCircle2 size={11} /> Verified
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            {user?.app_metadata?.provider === 'google' ? 'Google Account' : 'Email Account'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Section: Contact Details ─────────────────────────────────────────────────

function ContactDetailsSection({ user }: { user: any }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<ContactForm>({
    fullName: user?.user_metadata?.full_name || '',
    phone: '',
    city: '',
    state: '',
  });
  const [savedForm, setSavedForm] = useState<ContactForm>({ ...form });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('user_profiles')
          .select('full_name, phone, city, state')
          .eq('id', user?.id)
          .single();
        if (data) {
          const loaded: ContactForm = {
            fullName: data.full_name || user?.user_metadata?.full_name || '',
            phone: data.phone || '',
            city: data.city || '',
            state: data.state || '',
          };
          setForm(loaded);
          setSavedForm(loaded);
        }
      } catch {
        // silently ignore — table may not have these columns yet
      }
    };
    if (user?.id) fetchProfile();
  }, [user?.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const supabase = createClient();
      await supabase.auth.updateUser({ data: { full_name: form.fullName } });
      await supabase.from('user_profiles').upsert({
        id: user?.id,
        full_name: form.fullName,
        phone: form.phone,
        city: form.city,
        state: form.state,
        updated_at: new Date().toISOString(),
      });
      setSavedForm({ ...form });
      setEditing(false);
      toast.success('Contact details updated');
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({ ...savedForm });
    setEditing(false);
  };

  const fields: { key: keyof ContactForm; label: string; icon: React.ReactNode; placeholder: string; type?: string }[] = [
    { key: 'fullName', label: 'Full Name', icon: <User size={15} />, placeholder: 'Your full name' },
    { key: 'phone', label: 'Phone Number', icon: <Phone size={15} />, placeholder: '+91 98765 43210', type: 'tel' },
    { key: 'city', label: 'City', icon: <MapPin size={15} />, placeholder: 'Mumbai' },
    { key: 'state', label: 'State', icon: <MapPin size={15} />, placeholder: 'Maharashtra' },
  ];

  return (
    <div className="bg-card rounded-2xl border border-border p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <User size={16} className="text-primary" />
          <h3 className="text-base font-semibold text-foreground">Contact Details</h3>
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 text-sm text-primary font-medium hover:underline"
          >
            <Edit3 size={14} /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <X size={14} /> Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 text-sm bg-primary text-white font-medium px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              <Save size={14} /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(({ key, label, icon, placeholder, type }) => (
          <div key={key}>
            <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
              {icon} {label}
            </label>
            {editing ? (
              <input
                type={type || 'text'}
                value={form[key]}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={placeholder}
                className="w-full text-sm text-foreground bg-background border border-border rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            ) : (
              <p className="text-sm text-foreground bg-muted/40 rounded-xl px-4 py-2.5 border border-border">
                {form[key] || <span className="text-muted-foreground/60">Not set</span>}
              </p>
            )}
          </div>
        ))}

        {/* Email — always read-only */}
        <div>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
            <Mail size={15} /> Email Address
          </label>
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/40 rounded-xl px-4 py-2.5 border border-border">
            <span className="flex-1 truncate">{user?.email || '—'}</span>
            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
              <CheckCircle2 size={10} /> Verified
            </span>
          </div>
        </div>

        {/* Member Since */}
        <div>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
            <Clock size={15} /> Member Since
          </label>
          <p className="text-sm text-foreground bg-muted/40 rounded-xl px-4 py-2.5 border border-border">
            {user?.created_at
              ? new Date(user.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
              : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Section: Notification Preferences ───────────────────────────────────────

function NotificationSection({ userId }: { userId: string }) {
  const [prefs, setPrefs] = useState<NotificationPrefs>({
    emailUpdates: true,
    smsAlerts: false,
    loanOffers: true,
    payoutAlerts: true,
    referralActivity: true,
    weeklyDigest: false,
  });
  const [saving, setSaving] = useState(false);

  const toggle = (key: keyof NotificationPrefs) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 600));
    setSaving(false);
    toast.success('Notification preferences saved');
  };

  const notifItems: { key: keyof NotificationPrefs; label: string; desc: string; icon: React.ReactNode }[] = [
    { key: 'emailUpdates', label: 'Email Updates', desc: 'Loan application status and account activity', icon: <Mail size={15} /> },
    { key: 'smsAlerts', label: 'SMS Alerts', desc: 'Critical alerts sent via text message', icon: <Smartphone size={15} /> },
    { key: 'loanOffers', label: 'Loan Offers & Deals', desc: 'New offers, rate drops, and promotions', icon: <Bell size={15} /> },
    { key: 'payoutAlerts', label: 'Payout Notifications', desc: 'Payout request status and confirmations', icon: <Bell size={15} /> },
    { key: 'referralActivity', label: 'Referral Activity', desc: 'When someone clicks or converts via your link', icon: <Bell size={15} /> },
    { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'Summary of your earnings and activity', icon: <Mail size={15} /> },
  ];

  return (
    <div className="bg-card rounded-2xl border border-border p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Bell size={16} className="text-primary" />
          <h3 className="text-base font-semibold text-foreground">Notification Preferences</h3>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 text-sm bg-primary text-white font-medium px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
        >
          <Save size={14} /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className="divide-y divide-border">
        {notifItems.map(({ key, label, desc, icon }) => (
          <div key={key} className="flex items-center justify-between py-3.5 first:pt-0 last:pb-0">
            <div className="flex items-start gap-3 min-w-0">
              <span className="mt-0.5 text-muted-foreground flex-shrink-0">{icon}</span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
              </div>
            </div>
            <button
              onClick={() => toggle(key)}
              aria-label={`Toggle ${label}`}
              className={`relative ml-4 w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none flex-shrink-0 ${
                prefs[key] ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                  prefs[key] ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Section: Account Security ────────────────────────────────────────────────

function SecuritySection({ user }: { user: any }) {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, newPass: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const isGoogleUser = user?.app_metadata?.provider === 'google';
  const emailVerified = !!user?.email_confirmed_at;
  const lastSignIn = user?.last_sign_in_at
    ? new Date(user.last_sign_in_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'Unknown';

  const handleChangePassword = async () => {
    if (!passwords.newPass || passwords.newPass !== passwords.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwords.newPass.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.updateUser({ password: passwords.newPass });
      if (error) throw error;
      toast.success('Password updated successfully');
      setPasswords({ current: '', newPass: '', confirm: '' });
      setShowPasswordForm(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const securityItems = [
    {
      icon: <CheckCircle2 size={15} className={emailVerified ? 'text-green-600' : 'text-amber-500'} />,
      label: 'Email Verification',
      desc: emailVerified ? 'Your email address is verified' : 'Email not yet verified',
      badge: emailVerified
        ? <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">Verified</span>
        : <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Pending</span>,
    },
    {
      icon: <Monitor size={15} className="text-muted-foreground" />,
      label: 'Last Sign-in',
      desc: lastSignIn,
      badge: null,
    },
    {
      icon: <Key size={15} className="text-muted-foreground" />,
      label: 'Login Method',
      desc: isGoogleUser ? 'Signed in with Google OAuth' : 'Email & Password',
      badge: null,
    },
  ];

  return (
    <div className="bg-card rounded-2xl border border-border p-6">
      <div className="flex items-center gap-2 mb-5">
        <Shield size={16} className="text-primary" />
        <h3 className="text-base font-semibold text-foreground">Account Security</h3>
      </div>

      {/* Security status items */}
      <div className="space-y-1 mb-5">
        {securityItems.map(({ icon, label, desc, badge }) => (
          <div key={label} className="flex items-center gap-3 p-3.5 rounded-xl bg-muted/30 border border-border/50">
            <span className="flex-shrink-0">{icon}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">{label}</p>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">{desc}</p>
            </div>
            {badge}
          </div>
        ))}
      </div>

      {/* Security actions */}
      <div className="space-y-2 mb-5">
        {!isGoogleUser && (
          <button
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-muted/60 border border-border/50 transition-colors group"
          >
            <div className="flex items-center gap-3 text-left">
              <Lock size={15} className="text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Change Password</p>
                <p className="text-xs text-muted-foreground mt-0.5">Update your account password</p>
              </div>
            </div>
            <ChevronRight size={14} className={`text-muted-foreground transition-transform ${showPasswordForm ? 'rotate-90' : 'group-hover:translate-x-0.5'}`} />
          </button>
        )}
        <button
          onClick={() => toast.info('Two-factor authentication coming soon')}
          className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-muted/60 border border-border/50 transition-colors group"
        >
          <div className="flex items-center gap-3 text-left">
            <Smartphone size={15} className="text-muted-foreground flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">Two-Factor Authentication</p>
              <p className="text-xs text-muted-foreground mt-0.5">Add an extra layer of security</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Coming Soon</span>
            <ChevronRight size={14} className="text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
          </div>
        </button>
        <button
          onClick={() => toast.info('Active sessions management coming soon')}
          className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-muted/60 border border-border/50 transition-colors group"
        >
          <div className="flex items-center gap-3 text-left">
            <Monitor size={15} className="text-muted-foreground flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">Active Sessions</p>
              <p className="text-xs text-muted-foreground mt-0.5">Manage devices where you are signed in</p>
            </div>
          </div>
          <ChevronRight size={14} className="text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* Password change form */}
      {showPasswordForm && !isGoogleUser && (
        <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
          <p className="text-sm font-semibold text-foreground">Change Password</p>
          {[
            { key: 'newPass' as const, label: 'New Password', placeholder: 'Min. 8 characters' },
            { key: 'confirm' as const, label: 'Confirm New Password', placeholder: 'Repeat new password' },
          ].map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">{label}</label>
              <div className="relative">
                <input
                  type={showPw[key] ? 'text' : 'password'}
                  value={passwords[key]}
                  onChange={(e) => setPasswords((prev) => ({ ...prev, [key]: e.target.value }))}
                  placeholder={placeholder}
                  className="w-full text-sm text-foreground bg-background border border-border rounded-xl px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((prev) => ({ ...prev, [key]: !prev[key] }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPw[key] ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          ))}
          {passwords.newPass && passwords.confirm && passwords.newPass !== passwords.confirm && (
            <p className="flex items-center gap-1.5 text-xs text-red-500">
              <AlertCircle size={13} /> Passwords do not match
            </p>
          )}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => { setShowPasswordForm(false); setPasswords({ current: '', newPass: '', confirm: '' }); }}
              className="flex-1 text-sm text-muted-foreground border border-border rounded-xl py-2 hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleChangePassword}
              disabled={saving || !passwords.newPass || passwords.newPass !== passwords.confirm}
              className="flex-1 text-sm bg-primary text-white font-medium rounded-xl py-2 hover:bg-blue-700 transition-colors disabled:opacity-60"
            >
              {saving ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        </div>
      )}

      {/* Danger zone */}
      <div className="mt-5 pt-5 border-t border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Danger Zone</p>
        <button
          onClick={() => toast.error('Account deletion requires contacting support')}
          className="w-full flex items-center justify-between p-4 rounded-xl hover:bg-red-50 border border-red-200/60 transition-colors group"
        >
          <div className="flex items-center gap-3 text-left">
            <LogOut size={15} className="text-red-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-600">Delete Account</p>
              <p className="text-xs text-muted-foreground mt-0.5">Permanently remove your account and data</p>
            </div>
          </div>
          <ChevronRight size={14} className="text-red-400 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const router = useRouter();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      router.push('/');
    }
  }, [user, loading, router]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <DirectoryTopbar />
        <div className="max-w-3xl mx-auto px-4 py-8 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-2xl border border-border p-6 animate-pulse">
              <div className="h-5 bg-muted rounded w-1/4 mb-4" />
              <div className="space-y-3">
                <div className="h-10 bg-muted rounded-xl" />
                <div className="h-10 bg-muted rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <DirectoryTopbar />
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Profile Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your personal details, notification preferences, and account security
          </p>
        </div>

        <div className="space-y-5">
          <ProfileHeader user={user} />
          <ContactDetailsSection user={user} />
          <NotificationSection userId={user.id} />
          <SecuritySection user={user} />
        </div>
      </main>
    </div>
  );
}
